/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { withRouter } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import Select from 'react-select';
import { useQuery, useMutation } from '@apollo/react-hooks';
import ReactTooltip from 'react-tooltip';
import Button from './Button';
import {
  CREATE_ORGANIZATION,
  UPLOAD_FILES,
  GET_TIMEZONES,
} from '../graphql/queries';
import { getErrorMessage } from '../util/ErrorUtil';
import Error from './Error';
import Input, { AddressInput, PhoneNumberInput } from './Input';
import { FadeIn } from './Animation';
import colors from '../styles/colors';
import { media } from '../util/MediaQuery';

const Content = styled.div`
  align-items: center;
  justify-content: center;
`;
const Form = styled.form`
  align-items: center;
  width: 100%;
  align-items: center;
  justify-content: center;
  background: ${colors.white};

  border-radius: 5px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  margin: auto;
  overflow-y: auto;
  ${media.desktop`
    &::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }
  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: grey;
    border-radius: 10px;
  }
    padding: 30px;
    width: 400px;
  `};

  ${media.mobile`
    height: 100vh;
    width: 100vw;
    padding: 10px;
  `};
  ${media.desktop`
    min-height: 85vh !important;
    max-height: 85vh !important; 
  `};
  ${media.tablet`
    min-height: 100vh;
    max-height: 100vh; 
  `};
`;

const TopText = styled.div`
  font-size: 2rem;
  text-align: center;
  padding-top: 10px;
`;

const DropzoneText = styled.div`
  font-weight: 500;
  font-size: 1.2rem;
  color: ${colors.seaGreen};
  text-decoration: underline;
  cursor: pointer;
`;

const DropzoneContainer = styled.div`
  width: fit-content;
  border: none;
`;

const Image = styled.div`
  margin-top: 20px;
  background-image: ${(props) => `url(${props.src})`};
  background-size: cover;
  background-position: center;
  background-origin: unset;
  border-radius: ${(props) => props.radius || '5px'};
  height: 100px;
  width: 157.89px;
  border: 1px solid ${colors.grey5};
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  margin-bottom: 15px;
`;

const Logo = styled.div`
  margin-top: 20px;
  height: 100px;
  width: 100px;
  border-radius: 50%;
  background-color: white;
  border: 1px solid ${colors.grey5};
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 15px;
`;

const ImageContainer = styled.img`
  width: 100%;
  height: auto;
`;

const UploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0px 15px;
`;

const FlexUpload = styled.div`
  width: 100%;
  justify-content: center;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
`;

const DropdownHeader = styled.div`
  font-size: 1.2rem;
  color: ${colors.grey3};
  margin: 30px 0px 10px 0px;
`;

const DropdownContainer = styled.div`
  font-size: 1.3rem;
  width: 100%;
`;

const InstructionsText = styled.p`
  padding: 15px 0;
  color: ${colors.grey3};
  font-size: 11px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;
const colourStyles = {
  menu: (base) => ({
    ...base,
    height: 'auto',
    position: 'absolute',
  }),
  menuPortal: (base) => ({
    // to popout dropdown options outside overflow
    ...base,
    zIndex: 9999,
  }),
};

function Organization({ history }) {
  const [name, setName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerPhoneNumber, setOwnerPhoneNumber] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [timeZoneObj, setTimeZoneObj] = useState({});
  const [logoUrl, setLogoUrl] = useState('');
  const [posterImageUrl, setPosterImageUrl] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isLogoUpload, setIsLogoUpload] = useState(false);
  const [isPosterUpload, setIsPosterUpload] = useState(false);
  const { data: timeszonesData } = useQuery(GET_TIMEZONES);

  useEffect(() => {
    if (timeszonesData) {
      setTimeZoneObj(timeszonesData.timezones.defaultTimezone);
    }
  }, [timeszonesData]);

  const [uploadFiles] = useMutation(UPLOAD_FILES, {
    onError(error) {
      console.log(error);
    },
    onCompleted(data) {
      console.log(data);
      if (isLogoUpload) {
        setLogoUrl(data.uploadFiles[0].url);
        setIsLogoUpload(false);
      }
      if (isPosterUpload) {
        setPosterImageUrl(data.uploadFiles[0].url);
        setIsPosterUpload(false);
      }
    },
  });
  const onDrop = useCallback(
    (acceptedFiles) => {
      uploadFiles({
        variables: {
          files: acceptedFiles,
        },
      });
    },
    [uploadFiles],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png',
  });
  const [createOrganization, { loading }] = useMutation(CREATE_ORGANIZATION, {
    variables: {
      name,
      ownerName,
      ownerEmail,
      ownerPhoneNumber,
      address: {
        address1,
        address2,
        city,
        state,
        zip,
      },
      logoUrl,
      posterImageUrl,
      timezoneId: timeZoneObj?.value,
    },
    onError(error) {
      setErrorMsg(getErrorMessage(error));
    },
    onCompleted(data) {
      localStorage.setItem(
        'CHECK_THIS_OUT_AUTH_TOKEN',
        data.createOrganization.token,
      );
      history.push('/enterPayment');
    },
  });

  return (
    <Content>
      <FadeIn duration={0.6}>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            if (!logoUrl) {
              setErrorMsg('A logo is required');
            } else if (!posterImageUrl) {
              setErrorMsg('A poster image is required');
            } else {
            createOrganization();
            }
          }}
        >
          <TopText>Tell us about your business.</TopText>
          <Input
            type="text"
            header="BUSINESS NAME"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            width="100%"
          />
          <Input
            type="text"
            header="OWNER NAME"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            required
            width="100%"
          />
          <Input
            margin="15px 0px 15px 0px"
            top
            type="email"
            header="OWNER EMAIL"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            required
            width="100%"
          />
          <PhoneNumberInput
            top
            type="tel"
            header="OWNER PHONE NUMBER"
            value={ownerPhoneNumber}
            onChange={(e) => setOwnerPhoneNumber(e.target.value)}
            width="calc(100%)"
            required
          />
          <AddressInput
            header="ADDRESS"
            address1Value={address1}
            address1OnChange={(e) => setAddress1(e.target.value)}
            address2Value={address2}
            address2OnChange={(e) => setAddress2(e.target.value)}
            cityValue={city}
            cityOnChange={(e) => setCity(e.target.value)}
            stateValue={state}
            stateOnChange={(e) => setState(e.target.value)}
            zipValue={zip}
            zipOnChange={(e) => setZip(e.target.value)}
            width="100%"
          />

          <DropdownContainer>
            <DropdownHeader>Time Zone</DropdownHeader>
            <Select
              defaultValue={timeszonesData?.timezones?.defaultTimezone}
              value={timeZoneObj}
              onChange={(val) => setTimeZoneObj(val)}
              options={timeszonesData?.timezones?.timezonesArray}
              style={colourStyles}
            />
          </DropdownContainer>
          <FlexUpload>
            <UploadContainer data-tip="200x200px">
              <Logo>
                <ImageContainer src={logoUrl} />
              </Logo>
              <DropzoneContainer {...getRootProps()}>
                <input {...getInputProps()} />
                <DropzoneText
                  onClick={() => {
                    setIsLogoUpload(true);
                    setIsPosterUpload(false);
                  }}
                >
                  Upload Logo
                </DropzoneText>
                <InstructionsText>
                  <span
                    style={{
                      fontWeight: '500',
                      color: 'black',
                    }}
                  >
                    Recommendation:{' '}
                  </span>
                  Upload a Logo with 200x200px dimensions.
                </InstructionsText>
              </DropzoneContainer>
            </UploadContainer>
            <UploadContainer data-tip="Max 1080px wide">
              <Image src={posterImageUrl} />
              <DropzoneContainer {...getRootProps()}>
                <input {...getInputProps()} />
                <DropzoneText
                  onClick={() => {
                    setIsLogoUpload(false);
                    setIsPosterUpload(true);
                  }}
                >
                  Upload Poster Image
                </DropzoneText>
              </DropzoneContainer>
              <InstructionsText>
                <span
                  style={{
                    fontWeight: '500',
                    color: 'black',
                  }}
                >
                  Recommendation:{' '}
                </span>{' '}
                Upload a cover image within 1080px wide & 3MB in size.{' '}
              </InstructionsText>
            </UploadContainer>
          </FlexUpload>
          <ButtonContainer>
            <Button
              margin="20px 0px 40px 0px"
              width="30%"
              text="CONTINUE"
              type="submit"
              loading={loading}
            />
          </ButtonContainer>

          <ReactTooltip place="right" type="dark" effect="solid" />
          {errorMsg && (
            <Error
              error={errorMsg}
              margin="-25px 0px 0px 0px"
              loading={loading}
              position="relative"
            />
          )}
        </Form>
      </FadeIn>
    </Content>
  );
}

export default withRouter(Organization);
